import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Loader2, Gift, Users, TrendingUp, TrendingDown, ShoppingCart, Percent, Calendar } from "lucide-react";

const PERIODS = [
  { value: 7, label: "7j" },
  { value: 30, label: "30j" },
  { value: 90, label: "90j" },
];

function Stat({ icon: Icon, label, value, sub, color = "#0A2540", testId }) {
  return (
    <div className="p-4 rounded-xl border border-[#CBD5E1] bg-white" data-testid={testId}>
      <div className="flex items-center gap-2 mb-1">
        <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
          <Icon className="w-3.5 h-3.5" style={{ color }} />
        </div>
        <p className="text-[10px] uppercase tracking-wide text-[#64748B] font-semibold">{label}</p>
      </div>
      <p className="text-xl font-bold text-[#0A2540]" style={{ fontFamily: 'Outfit' }}>{value}</p>
      {sub && <p className="text-[10px] text-[#64748B] mt-0.5">{sub}</p>}
    </div>
  );
}

const fmtXAF = (n) => `${(n || 0).toLocaleString()} XAF`;

export default function PromoAnalyticsPanel() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/admin/stats/promos?days=${days}`)
      .then(({ data }) => { if (alive) setData(data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [days]);

  return (
    <div className="bg-white rounded-xl border border-[#CBD5E1] p-5" data-testid="admin-promo-analytics">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Gift className="w-4 h-4 text-[#FACC15]" />
          <h3 className="text-sm font-bold text-[#0A2540] uppercase tracking-wide" style={{ fontFamily: 'Outfit' }}>
            Promo Bienvenue · -15% sur 1<sup>ère</sup> location
          </h3>
        </div>
        <div className="flex gap-1 bg-[#F8FAFC] border border-[#CBD5E1] rounded-full p-0.5" data-testid="promo-period-selector">
          {PERIODS.map((p) => (
            <button
              key={p.value}
              onClick={() => setDays(p.value)}
              className={`px-3 py-1 text-[11px] font-semibold rounded-full transition-colors ${days === p.value ? "bg-[#0A2540] text-white" : "text-[#64748B] hover:text-[#0A2540]"}`}
              data-testid={`promo-period-${p.value}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#0A2540]" /></div>
      ) : !data ? (
        <div className="py-8 text-center text-xs text-[#64748B]">Aucune donnée disponible</div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            <Stat
              icon={Users}
              label="Promos émises"
              value={(data.issued_window || 0).toLocaleString()}
              sub={`${data.issued_total || 0} au total`}
              color="#3B82F6"
              testId="promo-stat-issued"
            />
            <Stat
              icon={ShoppingCart}
              label="Promos utilisées"
              value={(data.used_window || 0).toLocaleString()}
              sub={`${data.used_total || 0} au total`}
              color="#10B981"
              testId="promo-stat-used"
            />
            <Stat
              icon={Percent}
              label="Taux de conversion"
              value={`${data.conversion_rate_window || 0}%`}
              sub={`Global : ${data.conversion_rate_total || 0}%`}
              color="#FACC15"
              testId="promo-stat-conversion"
            />
            <Stat
              icon={TrendingDown}
              label="Manque à gagner"
              value={fmtXAF(data.lost_revenue_window)}
              sub={`${fmtXAF(data.lost_revenue_total)} au total`}
              color="#EF4444"
              testId="promo-stat-lost"
            />
            <Stat
              icon={Calendar}
              label="Réservations avec promo"
              value={(data.bookings_with_promo_window || 0).toLocaleString()}
              sub={`${data.bookings_with_promo_total || 0} au total`}
              color="#0A2540"
              testId="promo-stat-bookings"
            />
            <Stat
              icon={TrendingUp}
              label="CA brut (avec promo)"
              value={fmtXAF(data.gross_revenue_promo_window)}
              sub={`Net : ${fmtXAF(data.net_revenue_promo_window)}`}
              color="#10B981"
              testId="promo-stat-gross"
            />
            <Stat
              icon={ShoppingCart}
              label="Panier moyen avec promo"
              value={fmtXAF(data.avg_basket_with_promo)}
              color="#3B82F6"
              testId="promo-stat-avg-basket-promo"
            />
            <Stat
              icon={ShoppingCart}
              label="Panier moyen sans promo"
              value={fmtXAF(data.avg_basket_without_promo)}
              sub={data.avg_basket_with_promo && data.avg_basket_without_promo ? (data.avg_basket_with_promo > data.avg_basket_without_promo ? `+${Math.round((data.avg_basket_with_promo / data.avg_basket_without_promo - 1) * 100)}% vs sans promo` : `${Math.round((data.avg_basket_with_promo / data.avg_basket_without_promo - 1) * 100)}% vs sans promo`) : null}
              color="#64748B"
              testId="promo-stat-avg-basket-no-promo"
            />
          </div>

          <p className="text-[10px] text-[#94A3B8] mt-3" data-testid="promo-period-label">
            Période : {days} derniers jours · Mise à jour temps réel
          </p>
        </>
      )}
    </div>
  );
}
